import { Box, Flex } from '@chakra-ui/react';
import {
  ContributorList,
  DateFilter,
  durationData,
  getActivityFilters,
  GoalsWithNesting,
  ISelect,
  PeriodType,
  removeEmptyQueryParams,
  SearchInputBar,
  TeamMenu,
  TeamTableContextProvider,
  useDashboardStore,
  useDebounce,
  useQueryState,
} from '@devd-client/devd/components';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { AuthActionTypes, getUser, useAuth } from '@devd-client/devd/auth';

interface ContributorTabProps {
  search: string;
  setSearch: (search: string) => void;
  team: string;
  setTeam: (team: string) => void;
}

export const ContributorTab = ({
  search,
  setSearch,
  team,
  setTeam,
}: ContributorTabProps) => {
  const [appState, dispatch] = useAuth();
  const { selected, setSelected } = useDashboardStore();
  const [startDate, setStartDate] = useQueryState('start');
  const [endDate, setEndDate] = useQueryState('end');
  const [duration, setDuration] = useQueryState('duration');
  const [sprintId, setSprintId] = useQueryState('sprintId');
  const [sprintName, setSprintName] = useQueryState('sprint');
  const initialSelectedTeam =
    team && team !== 'Organisation'
      ? { value: team, label: team }
      : { value: '', label: 'Organisation' };
  const [searchTerm, setSearchTerm] = useState(search || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectedTeam, setSelectedTeam] =
    useState<ISelect>(initialSelectedTeam);

  const [periodOptions, setPeriodOptions] = useState(durationData);

  useEffect(() => {
    team &&
      setSelectedTeam(
        team === 'Organisation'
          ? { value: '', label: 'Organisation' }
          : { value: team, label: team }
      );

    if (search) {
      setSearchTerm(search);
    }

    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  useEffect(() => {
    if (typeof selected !== 'undefined') {
      selected.startDate && setStartDate(selected.startDate);
      selected.endDate && setEndDate(selected.endDate);
      selected.duration && setDuration(selected.duration);
      selected?.sprintId === ''
        ? removeEmptyQueryParams({ sprintId: '' })
        : setSprintId(selected?.sprintId);
      selected?.sprintName === ''
        ? removeEmptyQueryParams({ sprint: '' })
        : setSprintName(selected?.sprintName);
    }

    if (typeof selectedTeam !== 'undefined') {
      if (selectedTeam.label === 'Organisation') {
        setTeam('');
        removeEmptyQueryParams({ team: '' });
      } else {
        setTeam(selectedTeam.value);
      }
    }

    if (typeof searchTerm !== 'undefined') {
      searchTerm === ''
        ? removeEmptyQueryParams({ search: '' })
        : setSearch(searchTerm);
    }
  }, [selected, selectedTeam.value, searchTerm]);

  const handleTeamSelection = (teamObj: ISelect) => {
    setSelectedTeam(teamObj);
    if (teamObj.value) {
      setTeam(teamObj.value);
    } else {
      removeEmptyQueryParams({ team: '' });
    }
  };

  return (
    <Box>
      <Flex gap={4} align={'flex-end'}>
        <SearchInputBar
          searchValue={searchTerm}
          setSearchValue={setSearchTerm}
          placeholder="Search Contributors"
          size={'sm'}
        />
        <TeamMenu
          selectedTeam={selectedTeam}
          handleSelectedTeam={handleTeamSelection}
          noDefault
          hideShowDetails
        />
        <Box ml={'auto'}>
          <DateFilter
            getUser={getUser}
            appState={appState}
            dispatch={dispatch}
            AuthActionTypes={AuthActionTypes}
          />
        </Box>
      </Flex>
      <ContributorList
        metricFilterState={[]}
        metricTooltips={[]}
        compareTo=""
        searchTerm={debouncedSearchTerm}
        teamState={selectedTeam.value ? [selectedTeam.value] : []}
        activityFilter={getActivityFilters('active')}
        repoState={[]}
        languageState={[]}
        jobRoleState={[]}
        orderBy=""
        orderColumn=""
        setOrderColumn={(column) => {
          // No ordering functionality needed in this context
        }}
        setOrderBy={(order) => {
          // No ordering functionality needed in this context
        }}
        selectedMetrics={[]}
        customComponentRenderers={[
          (contributorEmails) => (
            <TeamTableContextProvider>
              <GoalsWithNesting
                associatedIds={contributorEmails}
                goalScope="CONTRIBUTOR"
              />
            </TeamTableContextProvider>
          ),
        ]}
        rowHeight="164px"
        hiddenColumns={['Languages', 'Knowledge Area', 'Team Allocation']}
      />
    </Box>
  );
};
